import { ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { HomeEntityChatNavigationState } from '../../models/home/entity-capabilities/home-entity-capabilities.model';
import { CsvTaskPollingService } from '../../services/chat/api/csv-task-polling.service';
import { ChatActionBuilderService } from '../../services/chat/presentation/chat-action-builder.service';
import { ChatDomEffectsService } from '../../services/chat/presentation/chat-dom-effects.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { FrontendLoggerService } from '../../services/frontend-logger.service';
import { LanguageService } from '../../services/language/language.service';
import { UserService } from '../../services/user.service';
import { formatChatError } from './chat-runtime.models';
import { ChatLifecycleStatePort } from './chat-runtime.ports';
import { ChatMessageController } from './chat-message.controller';
import { ChatRequestTracker } from './chat-request.tracker';
import { ChatRoleComposerController } from './chat-role-composer.controller';
import { ChatSessionController } from './chat-session.controller';

export class ChatLifecycleController {
  constructor(
    private readonly state: ChatLifecycleStatePort,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly storage: ChatStorageService,
    private readonly polling: CsvTaskPollingService,
    private readonly actions: ChatActionBuilderService,
    private readonly userService: UserService,
    private readonly language: LanguageService,
    private readonly logger: FrontendLoggerService,
    private readonly dom: ChatDomEffectsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly roles: ChatRoleComposerController,
    private readonly requests: ChatRequestTracker,
    private readonly sessions: ChatSessionController,
    private readonly messages: ChatMessageController
  ) {
    this.subscribeToAvatar();
    this.state.selectedMessageId = this.storage.getSelectedMessageId();
  }

  initialize(): void {
    this.state.buttonText = this.language.translate('chat.tools') || 'Tools';
    this.storage.clearSelectedMessageId();
    this.storage.setLastRoute('/chat');
    this.state.chatForm = this.formBuilder.group({
      messageInput: ['', Validators.required],
    });
    this.prefillFromHomeEntity();
    this.logger.info('Inicializando ChatComponent', 'ChatComponent.ngOnInit');
    this.loadLimitedDocument();
    this.roles.load(this.state.destroy$);
    this.sessions.loadInitial(
      () => this.roles.generateMessageEntityId(),
      (result) => this.sessions.applyInitial(result)
    );
    this.polling.systemMessage$.pipe(takeUntil(this.state.destroy$)).subscribe((message) => {
      this.logger.info(
        'Mensaje del sistema recibido del polling CSV',
        'ChatComponent.ngOnInit'
      );
      this.state.messages.push(this.actions.buildCsvPollingMessage(
        message,
        this.roles.generateMessageEntityId()
      ));
      this.sessions.scrollToBottom();
    });
  }

  destroy(): void {
    this.state.destroy$.next();
    this.state.destroy$.complete();
    this.messages.disposeCursorHint();
    try {
      this.dom.removeBodyClass('no-scroll');
    } catch (error) {
      this.logger.error(
        'Error al quitar clase no-scroll del body en ngOnDestroy: '
          + formatChatError(error),
        'ChatComponent.ngOnDestroy'
      );
      this.logger.error(
        `[ChatComponent] Error al quitar clase no-scroll del body en ngOnDestroy: ${JSON.stringify(error)}`
      );
    }
    this.state.activeRequests = 0;
    this.state.isRequesting = false;
    this.changeDetector.markForCheck();
  }

  private subscribeToAvatar(): void {
    try {
      this.userService.currentUser$.pipe(takeUntil(this.state.destroy$)).subscribe((user) => {
        this.state.userAvatarUrl = user?.avatar || 'assets/images/default-avatar.png';
        this.changeDetector.markForCheck();
      });
    } catch {
      // Avatar is optional and must not block chat startup.
    }
  }

  private loadLimitedDocument(): void {
    try {
      const limited = this.storage.getLimitedDocument();
      if (!limited) return;
      this.state.limitedDocValue = limited.value;
      this.state.limitedDocId = limited.id;
      this.state.limitedDocName = limited.name;
      this.logger.info(
        `Chat limitado al documento: ${limited.id} nombre: ${limited.name}`,
        'ChatComponent.ngOnInit'
      );
    } catch {
      this.logger.warn(
        'No se pudo leer limitChatDocId de localStorage',
        'ChatComponent.ngOnInit'
      );
    }
  }

  private prefillFromHomeEntity(): void {
    try {
      const getCurrentNavigation = this.router.getCurrentNavigation;
      const navigationState = typeof getCurrentNavigation === 'function'
        ? getCurrentNavigation.call(this.router)?.extras?.state
        : undefined;
      const browserState = typeof history === 'undefined' ? undefined : history.state;
      const state = (navigationState ?? browserState) as HomeEntityChatNavigationState | undefined;
      const text = state?.homeIaPrefill?.text;
      if (!text) return;
      this.state.chatForm.get('messageInput')?.setValue(text);
      this.dom.focusInputAtEnd('chat-input-message');
    } catch (error) {
      this.logger.warn(
        'No se pudo precargar el texto de entidad en el chat: ' + formatChatError(error),
        'ChatComponent.ngOnInit'
      );
    }
  }
}
